import React, { useEffect, useRef, RefObject } from 'react';

import { useLayoutContext } from '@ott/layout-context';

import FadeIn from 'src/components/elements/FadeIn';
import GradientButton from 'src/components/elements/GradientButton';
import SimpleSlider from 'src/components/elements/SimpleSlider';

import metrics from 'src/utility/metrics';

import { APP_URL, S3_MP4_URL } from '../../constants';
import { PLANETS } from './constants';

import { motion, useInView, useAnimation } from 'framer-motion';

import styles from './PlanetsSlider.scss';

const PlanetsSlider = () => {
  const { isDesktop } = useLayoutContext();

  const videoRef: RefObject<HTMLVideoElement> = useRef(null);
  const inViewport = useInView(videoRef);
  const controls = useAnimation();

  const variants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { delay: 2, duration: 1.5 } },
  };

  const sliderSettings = {
    centerMode: isDesktop,
    slidesToShow: isDesktop ? 5 : 1,
    autoplay: true,
    autoplaySpeed: isDesktop ? 8000 : 6000,
    speed: 1500,
    arrows: false,
    draggable: false,
    pauseOnHover: false,
    pauseOnFocus: false,
  };

  const downloadClick = (event) => {
    event.preventDefault();
    metrics('SOLAR_LANDING_INSTALL_BUTTON', { trigger: 'planets slider block' });
    window.open(APP_URL);
  };

  useEffect(() => {
    if (inViewport) {
      controls.start('animate');
    }
  }, [controls, inViewport]);

  return (
    <div className={styles.planetsSlider}>
      <div className={styles.title}>
        <FadeIn>
          <span>Digital arts</span>
        </FadeIn>
        <FadeIn delay={1}>
          <span className={styles.titleThin}>for your journeys</span>
        </FadeIn>
      </div>

      <FadeIn className={styles.description} delay={1.5}>
        <span>
          Limited NFT-based rewards with
          <br /> cosmic digital collectibles
        </span>
      </FadeIn>

      <motion.video
        ref={videoRef}
        autoPlay={true}
        preload="auto"
        loop={true}
        muted={true}
        playsInline={true}
        className={styles.video}
        variants={variants}
        initial="initial"
        animate={controls}
      >
        <source src={`${S3_MP4_URL}planets_slider.mp4`} type="video/mp4" />
      </motion.video>

      <FadeIn delay={2}>
        <SimpleSlider className={styles.slider} settings={sliderSettings}>
          {PLANETS.map((planet) => (
            <div key={planet.id} className={styles.slide}>
              <img key={planet.id} src={planet.src} alt="planet" />
              <div className={styles.slideContent}>
                <div className={styles.slideName}>{planet.name}</div>
                <div className={styles.slideText}>{planet.text}</div>
              </div>
            </div>
          ))}
        </SimpleSlider>
      </FadeIn>

      {!isDesktop && (
        <GradientButton
          className={styles.downloadButton}
          darkGradient={true}
          onClick={downloadClick}
        >
          Download app
        </GradientButton>
      )}
    </div>
  );
};

export default PlanetsSlider;
